"use client";

import { cn } from "@repo/ui";
import { useReducedMotion } from "motion/react";
import {
	type CSSProperties,
	type ReactNode,
	useEffect,
	useRef,
	useState,
} from "react";

export type QuizTruncationScrollMode = "horizontal" | "vertical";
export type QuizTruncationScrollLoopMode = "reset" | "reverse";

export interface QuizTruncationScrollProps {
	mode: QuizTruncationScrollMode;
	/**
	 * Vertical only: hard ceiling in line boxes (uses 1.375 line-height).
	 * Prefer this over Tailwind `max-h-*` so overflow measurement is stable.
	 * Ignored while {@link fillAvailable} and the parent already fits the text.
	 */
	maxLines?: number;
	/**
	 * Vertical only: fill the parent height. Apply {@link maxLines} only when
	 * content still overflows that space (cramped tiles) — otherwise show all
	 * lines the block can hold.
	 */
	fillAvailable?: boolean;
	/** Pixels per second along the scroll axis. */
	speed?: number;
	/** Pause at end / start before next loop leg (same duration both ends). */
	pauseMs?: number;
	loopMode?: QuizTruncationScrollLoopMode;
	/** Initial settle at start before first move (defaults to pauseMs). */
	pauseAtStartMs?: number;
	disabled?: boolean;
	className?: string;
	children: ReactNode;
	title?: string;
	"data-testid"?: string;
}

/**
 * Auto-scroll when content overflows — Prefer-over id `quiz-truncation-scroll`.
 * Idle when not truncated. Honors prefers-reduced-motion (no auto-scroll).
 *
 * Uses a float scroll accumulator — browsers quantize `scrollTop`/`scrollLeft`
 * to integers, so sub-pixel `+= delta` stalls at low speeds.
 */
export function QuizTruncationScroll({
	mode,
	maxLines,
	fillAvailable = false,
	speed = 40,
	pauseMs = 1800,
	loopMode = "reset",
	pauseAtStartMs,
	disabled = false,
	className,
	children,
	title,
	"data-testid": dataTestId = "quiz-truncation-scroll",
}: QuizTruncationScrollProps) {
	const startPauseMs = pauseAtStartMs ?? pauseMs;
	const prefersReducedMotion = useReducedMotion() ?? false;
	const viewportRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	const [overflowing, setOverflowing] = useState(false);
	const [fullText, setFullText] = useState<string | undefined>(title);
	/** When fillAvailable: only enforce maxLines once content overflows the parent. */
	const [applyMaxLinesCap, setApplyMaxLinesCap] = useState(
		() => !(fillAvailable && mode === "vertical"),
	);

	const maxLinesStyle: CSSProperties | undefined =
		mode === "vertical" &&
		maxLines != null &&
		maxLines > 0 &&
		applyMaxLinesCap
			? { maxHeight: `calc(${maxLines} * 1.375em)` }
			: fillAvailable && mode === "vertical"
				? { maxHeight: "100%" }
				: undefined;

	useEffect(() => {
		const viewport = viewportRef.current;
		const content = contentRef.current;
		if (!viewport || !content) {
			return;
		}

		function measure() {
			if (!viewport || !content) {
				return;
			}
			if (mode === "vertical" && fillAvailable) {
				const parent = viewport.parentElement;
				const availablePx =
					parent?.clientHeight ?? viewport.clientHeight;
				const contentPx = Math.max(
					content.scrollHeight,
					content.offsetHeight,
				);
				const fits = contentPx <= availablePx + 1;
				const fontSizePx =
					Number.parseFloat(getComputedStyle(content).fontSize) || 15;
				const lineBoxPx = fontSizePx * 1.375;
				const cramped =
					maxLines != null &&
					maxLines > 0 &&
					availablePx <= maxLines * lineBoxPx + 1;
				setApplyMaxLinesCap((prev) => {
					const next = !fits && cramped;
					return prev === next ? prev : next;
				});
			} else {
				setApplyMaxLinesCap(maxLines != null && maxLines > 0);
			}
			const overflow =
				mode === "horizontal"
					? Math.max(viewport.scrollWidth, content.scrollWidth) >
						viewport.clientWidth + 1
					: Math.max(viewport.scrollHeight, content.scrollHeight) >
						viewport.clientHeight + 1;
			setOverflowing((prev) => (prev === overflow ? prev : overflow));
			if (!title) {
				setFullText(content.textContent?.trim() || undefined);
			}
		}

		measure();
		const raf = requestAnimationFrame(() => {
			measure();
			requestAnimationFrame(measure);
		});
		const observer = new ResizeObserver(measure);
		observer.observe(viewport);
		observer.observe(content);
		const parent = viewport.parentElement;
		if (parent) {
			observer.observe(parent);
		}
		const container = viewport.closest("[data-experience-mode]");
		if (container) {
			observer.observe(container);
		}
		return () => {
			cancelAnimationFrame(raf);
			observer.disconnect();
		};
	}, [mode, children, title, maxLines, fillAvailable]);

	const canAutoScroll =
		overflowing && !disabled && !prefersReducedMotion && speed > 0;

	useEffect(() => {
		const viewport = viewportRef.current;
		if (!viewport || !canAutoScroll) {
			if (viewport) {
				viewport.scrollLeft = 0;
				viewport.scrollTop = 0;
			}
			return;
		}

		let raf = 0;
		let last = performance.now();
		let direction: 1 | -1 = 1;
		/** Float position — DOM scroll APIs snap to integers and would stall. */
		let scrollPos = 0;
		/** `reset` loop: pause at end, then jump to start and pause again. */
		let resetPhase: "scroll" | "end-pause" | "start-pause" = "start-pause";
		let pauseUntil = performance.now() + startPauseMs;

		function applyScroll() {
			if (!viewport) {
				return;
			}
			if (mode === "horizontal") {
				viewport.scrollLeft = scrollPos;
			} else {
				viewport.scrollTop = scrollPos;
			}
		}

		function maxScroll(): number {
			if (!viewport) {
				return 0;
			}
			return mode === "horizontal"
				? Math.max(0, viewport.scrollWidth - viewport.clientWidth)
				: Math.max(0, viewport.scrollHeight - viewport.clientHeight);
		}

		function tick(now: number) {
			if (!viewport) {
				return;
			}
			const max = maxScroll();
			if (max <= 0) {
				raf = requestAnimationFrame(tick);
				return;
			}

			if (now < pauseUntil) {
				last = now;
				raf = requestAnimationFrame(tick);
				return;
			}

			if (loopMode === "reset" && resetPhase === "end-pause") {
				scrollPos = 0;
				applyScroll();
				resetPhase = "start-pause";
				pauseUntil = now + pauseMs;
				last = now;
				raf = requestAnimationFrame(tick);
				return;
			}

			if (loopMode === "reset" && resetPhase === "start-pause") {
				resetPhase = "scroll";
				direction = 1;
				last = now;
			}

			const dt = Math.min(0.064, Math.max(0, (now - last) / 1000));
			last = now;
			scrollPos += speed * dt * direction;
			if (scrollPos < 0) {
				scrollPos = 0;
			}
			if (scrollPos > max) {
				scrollPos = max;
			}
			applyScroll();

			const atEnd = scrollPos >= max - 0.5;
			const atStart = scrollPos <= 0.5;

			if (direction === 1 && atEnd) {
				if (loopMode === "reverse") {
					direction = -1;
					pauseUntil = now + pauseMs;
				} else {
					scrollPos = max;
					applyScroll();
					resetPhase = "end-pause";
					pauseUntil = now + pauseMs;
				}
			} else if (direction === -1 && atStart) {
				direction = 1;
				pauseUntil = now + pauseMs;
			}

			raf = requestAnimationFrame(tick);
		}

		raf = requestAnimationFrame(tick);
		return () => {
			cancelAnimationFrame(raf);
			viewport.scrollLeft = 0;
			viewport.scrollTop = 0;
		};
	}, [canAutoScroll, speed, pauseMs, startPauseMs, loopMode, mode]);

	const isHorizontal = mode === "horizontal";
	const allowManualScroll = overflowing && (prefersReducedMotion || disabled);

	return (
		<div
			ref={viewportRef}
			style={maxLinesStyle}
			className={cn(
				"min-w-0",
				isHorizontal
					? cn(
							allowManualScroll
								? "overflow-x-auto whitespace-nowrap"
								: "overflow-x-hidden whitespace-nowrap",
							overflowing &&
								!canAutoScroll &&
								!allowManualScroll &&
								"truncate",
						)
					: allowManualScroll
						? "overflow-y-auto"
						: "overflow-y-hidden",
				className,
			)}
			title={fullText}
			data-testid={dataTestId}
			data-mode={mode}
			data-max-lines={maxLines != null ? String(maxLines) : undefined}
			data-fill-available={fillAvailable ? "true" : undefined}
			data-max-lines-capped={applyMaxLinesCap ? "true" : "false"}
			data-overflowing={overflowing ? "true" : "false"}
			data-scrolling={canAutoScroll ? "true" : "false"}
			data-loop-mode={loopMode}
		>
			<div
				ref={contentRef}
				className={cn(
					isHorizontal
						? "inline-block max-w-none"
						: "block w-full whitespace-normal break-words",
				)}
			>
				{children}
			</div>
		</div>
	);
}
